import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { uploadFile, absoluteUrl } from "@/lib/uploads";
import { TrendingUp, Sparkles, Video, Send, Upload, Edit3, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import VideoEditorModal from "@/components/VideoEditorModal";

const EMPTY_CHART = [
  { d: "Mon", v: 0 }, { d: "Tue", v: 0 }, { d: "Wed", v: 0 },
  { d: "Thu", v: 0 }, { d: "Fri", v: 0 }, { d: "Sat", v: 0 }, { d: "Sun", v: 0 },
];

export default function CreatorZone() {
  const [coachMsgs, setCoachMsgs] = useState([
    { role: "assistant", text: "Welcome to your studio. Ask me about audience growth, post timing, or content angles." },
  ]);
  const [coachInput, setCoachInput] = useState("");
  const [coachBusy, setCoachBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [reels, setReels] = useState([]);
  const [stats, setStats] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const [reelTitle, setReelTitle] = useState("");
  const [reelVideo, setReelVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const loadReels = async () => {
    const r = await api.get("/posts", { params: { zone: "creator" } });
    setReels(r.data);
  };

  const loadStats = async () => {
    try {
      const r = await api.get("/creator/stats");
      setStats(r.data);
    } catch (e) {
      console.warn("[CreatorZone] stats failed", e);
    }
  };

  useEffect(() => { loadReels(); loadStats(); }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [coachMsgs]);

  const askCoach = async () => {
    if (!coachInput.trim() || coachBusy) return;
    const text = coachInput.trim();
    setCoachInput("");
    setCoachMsgs((m) => [...m, { role: "user", text }]);
    setCoachBusy(true);
    try {
      const r = await api.post("/ai/chat", {
        message: text,
        provider: "openai",
        session_id: sessionId || undefined,
        system: "You are a Creator Growth Coach. Be direct, tactical, use bullets. Focus on virality, audience psychology, retention, and revenue per engagement quality.",
      });
      setSessionId(r.data.session_id);
      setCoachMsgs((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch { toast.error("Coach unreachable"); }
    setCoachBusy(false);
  };

  const chartData = stats?.chart_7d?.length ? stats.chart_7d : EMPTY_CHART;
  const max = Math.max(1, ...chartData.map((c) => c.v));

  const onPickVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await uploadFile(f);
      setReelVideo(r.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const publishReel = async () => {
    if (!reelVideo) { toast.error("Add a video first"); return; }
    setPublishing(true);
    try {
      await api.post("/posts", {
        content: reelTitle || "Untitled reel",
        video_url: reelVideo,
        zone: "creator",
        media_type: "reel",
      });
      setReelTitle(""); setReelVideo(null);
      await loadReels();
      toast.success("Reel published");
    } catch { toast.error("Publish failed"); }
    setPublishing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-1">creator economy</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Your Growth Lab</h1>
          <p className="text-[#64748B] text-sm mt-1">Revenue per engagement quality, not per view.</p>
        </div>
        <Link
          to="/analytics/posts"
          data-testid="open-post-analytics"
          className="nexus-btn-secondary text-sm flex items-center gap-2"
        >
          <BarChart3 size={14} /> Post analytics deep-dive
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Stats row */}
        <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="creator-stats-row">
          {[
            {
              l: "Quality Score",
              v: stats?.quality_score == null ? "—" : String(stats.quality_score),
              c: "#00E5FF",
              testid: "stat-quality-score",
            },
            {
              l: "Earnings (7d)",
              v: stats ? `$${(stats.earnings_7d || 0).toFixed(2)}` : "—",
              c: "#F5C542",
              testid: "stat-earnings-7d",
            },
            {
              l: "Subscribers",
              v: stats ? String(stats.subscribers_active || 0) : "—",
              c: "#7B2CBF",
              testid: "stat-subscribers",
            },
            {
              l: "Reactions",
              v: stats ? String(stats.reactions_lifetime || 0) : "—",
              c: "#0F172A",
              testid: "stat-reactions",
            },
          ].map((s) => (
            <div key={s.l} data-testid={s.testid} className="glass rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{s.l}</div>
              <div className="font-display text-3xl font-bold mt-1" style={{ color: s.c }}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="lg:col-span-8 glass-heavy rounded-3xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-[#00E5FF]" />
            <div className="font-display font-bold">Engagement quality (last 7 days)</div>
          </div>
          <div className="flex items-end justify-between gap-2 h-44" data-testid="engagement-chart">
            {chartData.map((c, i) => (
              <motion.div
                key={c.d}
                initial={{ height: 0 }}
                animate={{ height: `${(c.v / max) * 100}%` }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: "easeOut" }}
                className="flex-1 flex flex-col items-center justify-end gap-2"
              >
                <div
                  className="w-full rounded-t-lg"
                  style={{
                    height: "100%",
                    background: "linear-gradient(180deg, #00E5FF 0%, #7B2CBF 100%)",
                    boxShadow: "0 0 14px rgba(0, 229, 255, 0.3)",
                  }}
                />
                <div className="text-xs text-[#94A3B8]">{c.d}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* AI growth coach */}
        <div className="lg:col-span-4 glass-heavy rounded-3xl p-5 flex flex-col" style={{ minHeight: 520 }}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-[#F5C542]" />
            <div className="font-display font-bold">AI Growth Coach</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1">
            {coachMsgs.map((m, i) => (
              <div key={`${m.role}-${i}-${(m.text || "").slice(0, 12)}`} className={`text-sm rounded-2xl px-3 py-2 ${
                m.role === "user"
                  ? "bg-[#00E5FF]/10 border border-[#00E5FF]/30 ml-6"
                  : "glass mr-6"
              }`}>{m.text}</div>
            ))}
            {coachBusy && <div className="text-xs text-[#64748B]">analyzing…</div>}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="coach-input"
              value={coachInput}
              onChange={(e) => setCoachInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askCoach()}
              placeholder="How can I grow faster?"
              className="nexus-input"
            />
            <button
              data-testid="coach-send"
              onClick={askCoach}
              disabled={coachBusy}
              className="nexus-btn-primary !py-2 !px-3"
            >
              <Send size={14} />
            </button>
          </div>
        </div>

        {/* Reel uploader */}
        <div className="lg:col-span-12 glass-heavy rounded-3xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Upload size={18} className="text-[#F5C542]" />
            <div className="font-display font-bold">Upload a new reel</div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-3">
              <input
                data-testid="reel-title"
                value={reelTitle}
                onChange={(e) => setReelTitle(e.target.value)}
                placeholder="Reel title or caption"
                className="nexus-input"
              />
              <input ref={fileRef} type="file" accept="video/*" data-testid="reel-file" className="hidden" onChange={onPickVideo} />
              <div className="flex gap-2 flex-wrap">
                <button data-testid="reel-pick" onClick={() => fileRef.current?.click()} disabled={uploading} className="nexus-btn-secondary text-sm flex items-center gap-2">
                  <Upload size={14} /> {uploading ? "Uploading…" : reelVideo ? "Replace video" : "Pick video"}
                </button>
                <button data-testid="reel-publish" onClick={publishReel} disabled={publishing || !reelVideo} className="nexus-btn-primary text-sm">
                  {publishing ? "…" : "Publish reel"}
                </button>
              </div>
            </div>
            <div className="lg:col-span-2">
              {reelVideo ? (
                <video src={absoluteUrl(reelVideo)} controls className="rounded-xl w-full max-h-72 bg-black" />
              ) : (
                <div className="glass rounded-xl h-48 flex items-center justify-center text-sm text-[#64748B]">
                  No video selected. MP4 / WebM up to 25MB.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reels gallery */}
        <div className="lg:col-span-12 glass-heavy rounded-3xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Video size={18} className="text-[#7B2CBF]" />
            <div className="font-display font-bold">Recent reels</div>
          </div>
          {reels.length === 0 ? (
            <div className="text-sm text-[#64748B]">No reels yet. Upload one above.</div>
          ) : (
            <div data-testid="reels-grid" className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {reels.map((r) => (
                <ReelCard
                  key={r.id}
                  reel={r}
                  onEdit={() => setEditingPost(r)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {editingPost && (
        <VideoEditorModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSaved={(updated) => {
            setReels((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
          }}
        />
      )}
    </div>
  );
}

function ReelCard({ reel, onEdit }) {
  const [mediaBroken, setMediaBroken] = useState(false);
  const hasVideo = !!reel.video_url && !mediaBroken;
  const hasImage = !!reel.image_url && !mediaBroken;

  return (
    <div className="glass rounded-xl p-3 relative">
      {hasVideo ? (
        <video
          src={absoluteUrl(reel.video_url)}
          controls
          poster={reel.cover_image_url || undefined}
          onError={() => setMediaBroken(true)}
          className="rounded-lg w-full h-40 object-cover bg-black"
          data-testid={`reel-video-${reel.id}`}
        />
      ) : hasImage ? (
        <img
          src={absoluteUrl(reel.image_url)}
          alt=""
          onError={() => setMediaBroken(true)}
          className="rounded-lg w-full h-40 object-cover"
        />
      ) : (
        <div
          data-testid={`reel-unavailable-${reel.id}`}
          className="rounded-lg w-full h-40 bg-gradient-to-br from-[#00E5FF]/15 to-[#7B2CBF]/15 flex flex-col items-center justify-center text-[#64748B] gap-1"
        >
          <Video size={20} className="opacity-50" />
          <div className="text-xs">{mediaBroken ? "Media unavailable" : "No preview"}</div>
        </div>
      )}
      {reel.video_url && !mediaBroken && (
        <button
          onClick={onEdit}
          data-testid={`edit-video-${reel.id}`}
          className="absolute top-4 right-4 w-8 h-8 rounded-full glass-active flex items-center justify-center"
          title="AI edit"
        >
          <Edit3 size={12} className="text-[#00E5FF]" />
        </button>
      )}
      <div className="text-sm mt-2 line-clamp-2">{reel.content}</div>
      {reel.captions && (
        <div className="text-xs text-[#F5C542] mt-1 line-clamp-1">&ldquo;{reel.captions}&rdquo;</div>
      )}
    </div>
  );
}
